import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
    usePopupListeners,
    useUnscrollableBody,
    useWindowDimensions,
    useWindowDimensionsHeight
} from '../../../../../../../common/utils/windowDimensions';
import ReactDOM from 'react-dom';
import { existLocales } from '../../../../../../../common/utils/dateLocales';
import UserContext from '../../../../../../../common/utils/getContext';
import CustomHeader from './CustomHeader/CustomHeader';
import moment from 'moment';
import PropTypes from 'prop-types';
import css from './CustomPicker.module.scss';
import classnames from 'classnames';
import { checkBrowser, getWithZoom } from '../../../../../../../common/utils/utils';

const CustomPicker = ({
    pickerElement,
    componentConfig,
    handlers = {},
    setFocused,
    attribute,
    value,
    showTimeSelect,
    execludeYear,
    lastYear
}) => {
    const { lang } = useContext(UserContext);
    const modalElement = useRef();
    const [langLocale, setLangLocale] = useState('ru');
    const heightDimensions = useWindowDimensionsHeight();
    const isMobile = useWindowDimensions() < 768;
    useEffect(() => {
        if (existLocales[lang]) {
            registerLocale(lang, existLocales[lang]);
            setLangLocale(lang);
        } else {
            (async () => {
                const locale = await import('date-fns/locale/ru');
                const localeDef = await locale;
                if (localeDef.default) {
                    registerLocale('ru', localeDef.default);
                }
            })();
        }
    }, [lang]);
    const func = useCallback(() => {
        if (!isMobile && modalElement.current) {
            // TODO ZOOM

            const picker = pickerElement.current.getBoundingClientRect();
            const modal = modalElement.current.getBoundingClientRect();
            let lPos = checkBrowser(picker.right) - checkBrowser(modal.width);
            if (lPos < 0) {
                lPos = picker.left;
            }
            let tPos = picker.top + getWithZoom(window?.scrollY) + checkBrowser(picker.height) + 10;
            if (tPos + modal.height > getWithZoom(window?.scrollY) + heightDimensions) {
                tPos = picker.top + getWithZoom(window?.scrollY) - modal.height - 10;
                if (tPos < getWithZoom(window?.scrollY)) {
                    tPos = picker.top + getWithZoom(window?.scrollY) + picker.height + 10;
                }
            }

            // TODO ZOOM

            modalElement.current.style.left = lPos + 'px';
            modalElement.current.style.top = tPos + 'px';
            modalElement.current.classList.add(css.visible);
        }
    }, [pickerElement, isMobile, heightDimensions]);

    const closeCalendal = useCallback(
        (v) => {
            if (v && componentConfig?.onChange) {
                componentConfig?.onChange(v);
            }
            if (v && componentConfig?.onSelect) {
                componentConfig?.onSelect(v);
            }
            setFocused(false);
            !!handlers?.onBlur &&
                handlers?.onBlur(
                    attribute,
                    value
                        ? moment(value, showTimeSelect ? 'DD.MM.yyyy HH:mm' : 'DD.MM.yyyy').format(
                              showTimeSelect ? 'yyyy-MM-DD HH:mm' : 'yyyy-MM-DD'
                          )
                        : null
                );
        },
        [attribute, componentConfig, handlers, setFocused, showTimeSelect, value]
    );
    useUnscrollableBody(isMobile, 'CustomPicker', closeCalendal);

    const config = {
        ...componentConfig,
        locale: langLocale,
        onChange: closeCalendal,
        inline: true,
        filterTime: (widgetTime) => {
            const maxDate = componentConfig?.maxDate;
            const minDate = componentConfig?.minDate;
            // TODO: implement time filtering

            if (minDate && maxDate) {
                return widgetTime?.getTime() > minDate?.getTime() && widgetTime?.getTime() < maxDate?.getTime();
            }

            if (minDate) {
                return widgetTime?.getTime() > minDate?.getTime();
            }

            if (maxDate) {
                return widgetTime?.getTime() < maxDate?.getTime();
            }
            return true;
        },
        renderCustomHeader: ({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled
        }) => (
            <CustomHeader
                execludeYear={execludeYear}
                changeYear={changeYear}
                changeMonth={changeMonth}
                decreaseMonth={decreaseMonth}
                increaseMonth={increaseMonth}
                prevMonthButtonDisabled={prevMonthButtonDisabled}
                nextMonthButtonDisabled={nextMonthButtonDisabled}
                date={date}
                lastYear={lastYear}
            />
        )
    };

    usePopupListeners(pickerElement, func, modalElement, closeCalendal, isMobile);
    return ReactDOM.createPortal(
        <div>
            <div
                onClick={() => {
                    closeCalendal();
                }}
                className={css.customDatePickerBackdrop}
            />
            <div
                className={classnames(css.customDatePickerModal)}
                style={{
                    width: showTimeSelect ? '340px' : '320px',
                    height: '270px'
                }}
                ref={modalElement}
            >
                <div className={classnames(css.customDatePicker)}>
                    <DatePicker {...config} />
                </div>
            </div>
        </div>,
        document?.body
    );
};
CustomPicker.propTypes = {
    pickerElement: PropTypes.object,
    componentConfig: PropTypes.object,
    handlers: PropTypes.object,
    setFocused: PropTypes.func,
    attribute: PropTypes.string,
    value: PropTypes.string,
    showTimeSelect: PropTypes.bool
};
export default CustomPicker;
